// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Source, Layer } from "react-map-gl/maplibre";
import { MapLibreLayerSpecification } from "../../../types";
import { getPaintPropertyExceptOfSymbolTypedLayer, getSymbolLayoutPropertiesWithSelection } from "./helpers";
import { computeSelectionBbox } from "../../../utilities/bboxUtils";
import { useConfig } from "../../../contexts/ConfigContext";
import { useSelectionActions, useSelectionStore } from "../../../store/selection";
import { useEffectiveVisibleLayers } from "../../../hooks/useEffectiveVisibleLayers";
import { stripCmSubLayerSuffix } from "../../../hooks/useCmTableMapLayers";

interface LayersProps {
  mapLibreLayers: MapLibreLayerSpecification[];
}

/**
 * Determines the MapLibre visibility value for a given layer.
 * When isFilteredByEndpoint is true, only layers explicitly in effectiveVisibleLayers show.
 * CM Table layers (prefixed "cm-table-") are always visible when not filtered,
 * matching the behaviour of useEffectiveVisibleLayers for dynamic layers.
 */
function getLayerVisibility(layerId: string, isFilteredByEndpoint: boolean, effectiveVisibleLayers: Set<string>): "visible" | "none" {
  // Mixed-geometry CM tables render as sibling layers (cm-table-<slug>::line /
  // ::fill) that share the base layer's visibility, so resolve to the base id.
  const resolvedId = stripCmSubLayerSuffix(layerId);
  if (isFilteredByEndpoint) {
    return effectiveVisibleLayers.has(resolvedId) ? "visible" : "none";
  }
  return effectiveVisibleLayers.has(resolvedId) ? "visible" : "none";
}

const Layers: React.FC<LayersProps> = ({ mapLibreLayers }) => {
  const [loadedSources, setLoadedSources] = useState<Record<string, boolean>>({});
  const decodeErrorCountBySourceRef = useRef<Record<string, number>>({});
  const selectedEntityId = useSelectionStore((state) => state.primary.selectedEntityId);
  const selectedEndpoint = useSelectionStore((state) => state.primary.selectedEndpoint);
  const secondaryIdsByLayer = useSelectionStore((state) => state.secondary.idsByLayer);
  const { setSecondaryItemsBbox } = useSelectionActions();
  // Effective visibility already combines per-layer toggles with the endpoint filter,
  // so both the map and the LayersPanel UI stay in sync on what is actually shown.
  const { effectiveVisibleLayers, isFilteredByEndpoint } = useEffectiveVisibleLayers();
  const config = useConfig();

  // Memoized to reduce re-render
  const { normalizedSelectedEntityId, selectedEntityLayerId } = useMemo(() => {
    const entityData = {
      normalizedSelectedEntityId: null,
      selectedEntityLayerId: null
    };
    entityData.normalizedSelectedEntityId = isNaN(Number(selectedEntityId)) ? selectedEntityId : Number(selectedEntityId);
    entityData.selectedEntityLayerId = selectedEndpoint?.mapLayerId || null;

    return entityData;
  }, [selectedEntityId, selectedEndpoint?.mapLayerId]);

  // Handle source loading errors using event listeners
  useEffect(() => {
    const map = (document.querySelector(".maplibregl-map") as any)?.map;
    if (!map) return;

    const extractUrlFromText = (text: string): string | null => {
      const match = text.match(/https?:\/\/[^\s'")]+/i);
      return match?.[0] || null;
    };

    const normalizeUrl = (value: string): string => {
      try {
        const u = new URL(value, window.location.origin);
        return `${u.origin}${u.pathname}`;
      } catch {
        return value.split("?")[0];
      }
    };

    const inferSourceIdFromError = (e: any): string | null => {
      const directSourceId = e?.sourceId || e?.source?.id;
      if (directSourceId) return directSourceId;

      const errorMessage = String(e?.error?.message || e?.message || "");
      const errorUrl = e?.url || e?.error?.url || extractUrlFromText(errorMessage);
      if (!errorUrl) return null;

      const normalizedErrorUrl = normalizeUrl(errorUrl);

      for (const layer of mapLibreLayers) {
        const source: any = (layer as any)?.source;
        if (!source) continue;

        const sourceCandidates: string[] = [];

        if (typeof source.url === "string") sourceCandidates.push(source.url);
        if (typeof source.data === "string") sourceCandidates.push(source.data);
        if (Array.isArray(source.tiles)) {
          source.tiles.forEach((t: any) => {
            if (typeof t === "string") sourceCandidates.push(t);
          });
        }

        const hasMatch = sourceCandidates.some((candidate) => {
          const normalizedCandidate = normalizeUrl(candidate);
          return (
            normalizedErrorUrl.includes(normalizedCandidate) ||
            normalizedCandidate.includes(normalizedErrorUrl) ||
            errorUrl.includes(candidate) ||
            candidate.includes(errorUrl)
          );
        });

        if (hasMatch) return layer.id;
      }

      return null;
    };

    const quarantineSource = (sourceId: string) => {
      setLoadedSources((prev) => {
        if (prev[sourceId] === false) return prev;
        return {
          ...prev,
          [sourceId]: false
        };
      });
    };

    const handleMapError = (e: any) => {
      const errorMessage = String(e?.error?.message || e?.message || "");
      const sourceId = inferSourceIdFromError(e);
      const isDecodeError = errorMessage.toLowerCase().includes("source image could not be decoded");

      if (!sourceId) {
        return;
      }

      if (isDecodeError) {
        decodeErrorCountBySourceRef.current[sourceId] = (decodeErrorCountBySourceRef.current[sourceId] || 0) + 1;
        if (decodeErrorCountBySourceRef.current[sourceId] >= 2) {
          quarantineSource(sourceId);
          return;
        }
      }
    };

    map.on("error", handleMapError);

    return () => {
      map.off("error", handleMapError);
    };
  }, [mapLibreLayers]);

  useEffect(() => {
    if (config?.autoFitToSecondarySelection === false) return;
    if (Object.keys(secondaryIdsByLayer).length > 0) {
      let secondaryItemsWithSelectedItem = { ...secondaryIdsByLayer };

      if (selectedEntityId && selectedEndpoint?.mapLayerId) {
        const layerId = selectedEndpoint.mapLayerId;
        const existingIds = secondaryItemsWithSelectedItem[layerId] || [];
        secondaryItemsWithSelectedItem[layerId] = Array.from(new Set([...existingIds, selectedEntityId]));
      }

      setSecondaryItemsBbox(computeSelectionBbox(secondaryItemsWithSelectedItem, mapLibreLayers));
    } else {
      setSecondaryItemsBbox(null);
    }
  }, [
    secondaryIdsByLayer,
    mapLibreLayers,
    setSecondaryItemsBbox,
    config?.autoFitToSecondarySelection,
    selectedEndpoint?.mapLayerId,
    selectedEntityId
  ]);

  // The transformToMapLibreLayer function in useMapLibreLayers.ts
  // already converts Fulcrum layers to GeoJSON layers,
  // so by the time we get here, all layer types will be
  // properly processed as GeoJSON or raster.
  //
  // Endpoint-filter ("Show only {endpoint} results on map") is applied via
  // `effectiveVisibleLayers` below — layers remain mounted but their
  // `layout.visibility` flips to `"none"`, which avoids source remount flicker
  // when the filter is toggled on and off.

  // MapLibre paint order follows insertion order: later layers draw on top.
  // Render WMS/raster sources first so GeoJSON vectors (Fulcrum, etc.) stay above reference rasters
  // such as "Pastoral Station Boundaries".
  //
  // NOTE: The final z-order (Base -> Raster -> Polygon -> Line -> Point, active layer on top)
  // is enforced imperatively by useMapLayerZOrder in MapComponent, because react-map-gl does
  // NOT reorder already-mounted layers when their JSX order changes.
  return (
    <>
      {mapLibreLayers
        .filter((layer) => layer.source.type === "raster")
        .map((layer) => {
          if (loadedSources[layer.id] === false) return null;

          return (
            <Source key={layer.id} id={layer.id} {...layer.source}>
              <Layer
                {...layer}
                id={layer.id}
                source={layer.id}
                layout={{
                  ...layer.layout,
                  visibility: getLayerVisibility(layer.id, isFilteredByEndpoint, effectiveVisibleLayers)
                }}
              />
            </Source>
          );
        })}
      {mapLibreLayers
        .filter((layer) => layer.source.type === "geojson")
        .map((layer) => {
          if (loadedSources[layer.id] === false) return null;

          // Only "find related" (secondary) selections dim the other entities in a layer.
          // A plain single selection (primary) must NOT grey out the rest of its table.
          const hasSecondaryForCurrentLayer = (secondaryIdsByLayer?.[layer.id]?.length ?? 0) > 0;
          const shouldPrioritizeCurrentLayer = hasSecondaryForCurrentLayer;

          return (
            // Fix #6: key must be on the outermost element, not on <Source> inside <>.
            // The type is part of the key: react-map-gl cannot change a mounted layer's
            // type (it warns and keeps the old one), so a layer redrawn as lines instead
            // of points (Draw as, in the org config) is remounted.
            <React.Fragment key={`${layer.id}:${layer.type}`}>
              <Source id={layer.id} {...layer.source}>
                {/* load all layers with updated values */}
                <Layer
                  {...layer}
                  id={layer.id}
                  source={layer.id}
                  layout={{
                    ...layer.layout,
                    ...getSymbolLayoutPropertiesWithSelection({
                      currentLayer: layer.id,
                      primarySelectionId: normalizedSelectedEntityId,
                      primarySelectionLayerId: selectedEntityLayerId,
                      secondarySelectionIds: secondaryIdsByLayer?.[layer.id] ?? [],
                      existingProperties: layer.layout,
                      layerType: layer.type,
                      primarySelectionKey: (layer as any).metadata?.isFulcrum ? "fulcrum_id" : "id",
                      enablePrioritization: shouldPrioritizeCurrentLayer
                    }),
                    visibility: getLayerVisibility(layer.id, isFilteredByEndpoint, effectiveVisibleLayers)
                  }}
                  paint={{
                    ...layer.paint,
                    ...getPaintPropertyExceptOfSymbolTypedLayer({
                      currentLayer: layer.id,
                      primarySelectionId: normalizedSelectedEntityId,
                      primarySelectionLayerId: selectedEntityLayerId,
                      secondarySelectionIds: secondaryIdsByLayer?.[layer.id] ?? [],
                      existingProperties: layer.paint,
                      layerType: layer.type,
                      primarySelectionKey: (layer as any).metadata?.isFulcrum ? "fulcrum_id" : "id",
                      enablePrioritization: shouldPrioritizeCurrentLayer
                    })
                  }}
                />

                {/* Extra symbol overlay disabled to reduce icon decode churn that can
                    cause repaint loops on heavy Fulcrum sessions. */}
              </Source>
            </React.Fragment>
          );
        })}
    </>
  );
};

export default Layers;
